import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changePassword } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, AlertCircle } from "lucide-react";

export default function ChangePasswordPage() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (next.length < 12) {
      setError("New password must be at least 12 characters.");
      return;
    }
    setBusy(true);
    const r = await changePassword(current, next);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    window.location.href = "/";
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 items-center text-center">
          <img src="/aquila-logo.svg" alt="Aquila" className="h-10 w-auto" />
          <CardTitle className="text-lg">Set a new password</CardTitle>
          <p className="text-xs text-muted-foreground">You must change your temporary password before continuing.</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="cur">Current (temporary) password</Label>
              <Input
                id="cur"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                disabled={busy}
                data-testid="cp-current"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                disabled={busy}
                data-testid="cp-new"
              />
              <p className="text-xs text-muted-foreground">
                Minimum 12 characters. Avoid common words and your name or email.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conf">Confirm new password</Label>
              <Input
                id="conf"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                disabled={busy}
                data-testid="cp-confirm"
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 border border-red-300/60 dark:bg-red-950/40 dark:border-red-700/40 px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" className="w-full" disabled={busy} data-testid="cp-submit">
              <Lock className="h-4 w-4 mr-2" />
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
